/*
 * Copyright (c) Nexign, JSC, 2020-2022.
 */
/**
 * Product Card Tariff Styled
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.10.0
 */

import styled from 'styled-components';

import { colors, fonts } from '@theme/index';

import { ProductCardStyled } from './ProductCardStyled';

/*
* -------------------------------------------
*           Tariff Card
* -------------------------------------------
*/

export const ProductCardTariffStyled = styled(ProductCardStyled)<{ height: string }>`
    height: ${props => props.height};
    padding: 32px 0;
    box-sizing: border-box;

    .product-card__title {
        padding: 0 16px;
    }
`;

export const ProductCardCampaignTariffStyled = styled(ProductCardTariffStyled)`
    padding: 20px;

    .product-card__title {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        flex-grow: 5;
        text-align: left;
        max-height: 120px;
        padding: 0;

        .arrow-tooltip__text {
            padding: 0 16px;
        }

        &:after {
            width: 100%;
            margin-top: 20px;
            border: 1px solid ${colors.borderColorDisabled};
            content: "";
        }
    }

    .product-card__field-wrapper {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        padding: 0;
        .product-card__field-roaming:first-child {
            margin-top: 0;
        }
        .product-card__field {
            margin-top: 24px;
            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    .product-card__price {
        margin-bottom: 0;

        &:before {
            width: 100%;
            margin-top: 32px;
            margin-bottom: 16px;
            border: 1px solid ${colors.borderColorDisabled};
            content: "";
        }
    }
`;


/*
* -------------------------------------------
*           Tariff Title
* -------------------------------------------
*/

export const ProductCardPersonalInfoStyled = styled.div`
    display: flex;
    flex-direction: row;
`;

export const ProductCardTariffTitleStyled = styled.div.attrs({
    className: 'product-card__title',
})`
    text-align: center;
    ${fonts.textHeader3};
    margin-bottom: 0;
`;

/*
* -------------------------------------------
*           Tariff Description
* -------------------------------------------
*/

export const ProductCardTariffDescriptionStyled = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    margin-top: 12px;
    margin-bottom: 24px;
    padding: 0 20px;
    text-align: center;
    ${fonts.textSmall};

    &:after {
        width: 100%;
        margin-top: 20px;
        border: 1px solid ${colors.borderColorDisabled};
        content: "";
    }
`;



/*
* -------------------------------------------
*           Tariff Range
* -------------------------------------------
*/

export const ProductCardTariffRangeStyled = styled.div.attrs({
    className: 'product-card__range',
})`
    margin-top: 32px;
`;

export const ProductCardTariffRangeParametersStyled = styled.div.attrs({
    className: 'product-card__range-parameters',
})`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0;
    padding: 0 16px;

    .text-block {
        margin-bottom: 0;
    }
`;

export const ProductCardTariffOneRangeParametersStyled = styled.div.attrs({
    className: 'product-card__one-range-parameters',
})`
    margin-bottom: 0;
    padding: 8px 20px 0;
    ${fonts.textHeader5};
`;


/*
* -------------------------------------------
*              Tariff Field
* -------------------------------------------
*/

export const ProductCardTariffFieldWrapperStyled = styled.div.attrs({
    className: 'product-card__field-wrapper',
})`
    flex-grow: 1;
    margin-top: 24px;
    padding: 0 20px;
`;

export const ProductCardTariffFieldStyled = styled.div.attrs({
    className: 'product-card__field',
})`
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
`;

export const ProductCardTariffFieldTitleStyled = styled.div.attrs({
    className: 'product-card__field-title',
})`
    ${fonts.textSmall};
    margin-bottom: 4px;
    color: rgba(51, 51, 51, 0.5);
`;

export const ProductCardTariffFieldRoamingStyled = styled.div.attrs({
    className: 'product-card__field-roaming',
})`
    display: flex;
    align-items: center;
    margin-top: 12px;

    .block-with-icon__icon-left {
        margin-right: 12px;
    }

    .field-roaming__tooltip {
        margin-left: 8px;
    }
`;

export const ProductCardTariffFieldInternetUnlimitedStyled = styled.div.attrs({
    className: 'product-card__field-internet-unlimited',
})`
    display: flex;
    flex-wrap: wrap;

    ${fonts.textSmall}

    & > div {
        margin-right: 4px;
    }
`;

/*
* -------------------------------------------
*              Tariff Price
* -------------------------------------------
*/

export const ProductCardTariffPriceStyled = styled.div.attrs({
    className: 'product-card__price',
})`
    display: flex;
    flex-direction: column;
    align-self: center;
    width: 100%;
    margin-bottom: 24px;
    text-align: center;

    .product-card__price-title {
        ${fonts.textHeader3};
        margin-bottom: 4px;
    }

    .product-card__price-subtitle {
        ${fonts.textSmall};
    }
`;

/*
* -------------------------------------------
*          Tariff Moore Details
* -------------------------------------------
*/


export const ProductCardTariffMooreDetailsStyled = styled.div.attrs({
    className: 'product-card__moore-details',
})`
    align-self: center;
`;
